import axios from "axios";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    MapPinned,
    Plus,
    Search,
    MapPin,
    Home,
    Building,
    Trash2,
    FileEdit,
    Map
} from 'lucide-react';
import BotonAcceso from "../elementos/BotonAcceso.jsx";
import { useTheme } from "../guardarToken/themeContext";
import { CircularProgress, Tooltip } from "@mui/material";

const apiUrlDirecciones = process.env.REACT_APP_API_DIRECCIONES || '/api/direcciones/addres/';

function AbmDirecciones() {
    const [direciones, setDirecciones] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const { isDarkMode } = useTheme();

    useEffect(() => {
        getaddres();
    }, []);

    const getaddres = async () => {
        try {
            setLoading(true);
            const res = await axios.get(apiUrlDirecciones);
            setDirecciones(res.data);
        } catch (error) {
            console.error("Error al intentar mostrar direcciones;", error);
        } finally {
            setLoading(false);
        }
    };

    const deleteAddres = async (id) => {
        if (!window.confirm("¿Estás seguro de que deseas eliminar esta dirección?")) return;
        try {
            await axios.delete(`${apiUrlDirecciones}${id}`);
            getaddres();
        } catch (error) {
            console.error("Error al intentar eliminar dirección;", error);
        }
    };

    const filteredDirecciones = direciones.filter((dir) => {
        const search = searchTerm.toLowerCase();
        return (
            dir.direccion?.toLowerCase().includes(search) ||
            dir.localidad?.toLowerCase().includes(search)
        );
    });

    if (loading) {
        return (
            <div className={`flex justify-center items-center h-screen ${isDarkMode ? "bg-slate-950" : "bg-slate-50"}`}>
                <CircularProgress color="warning" />
            </div>
        );
    }

    return (
        <div className={`min-h-screen p-3 lg:p-4 font-sans transition-colors duration-300 ${isDarkMode ? "bg-slate-950 text-white" : "bg-slate-50 text-slate-800"}`}>

            {/* HEADER PREMIUM */}
            <div className={`${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200"} rounded-2xl shadow-sm p-5 mb-6 flex flex-col md:flex-row items-center justify-between border ring-1 ring-black/5 animate-in slide-in-from-top duration-700`}>
                <div className="flex items-center gap-4">
                    <div className="bg-gradient-to-br from-emerald-500 to-emerald-700 p-3 rounded-xl shadow-lg shadow-emerald-900/20">
                        <MapPinned className="text-white w-6 h-6" />
                    </div>
                    <div>
                        <h1 className="text-xl font-black tracking-tight leading-none mb-1">Gestión de Direcciones</h1>
                        <p className={`text-[11px] ${isDarkMode ? "text-slate-400" : "text-slate-500"} font-black uppercase tracking-wider`}>Administra las sedes y ubicaciones físicas</p>
                    </div>
                </div>

                <div className="flex items-center gap-3 mt-4 md:mt-0">
                    <div className="relative w-48 md:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Buscar ubicación..."
                            style={{ paddingLeft: '48px' }}
                            className={`w-full pr-4 py-2 rounded-xl text-xs font-bold focus:outline-none transition-all ${isDarkMode ? "bg-slate-950 border-slate-800 text-white focus:border-emerald-500/50" : "bg-slate-50 border-slate-200 text-slate-800 focus:border-emerald-500"
                                } border`}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <BotonAcceso
                        variant="accent"
                        onClick={() => navigate('/admin/CreateAddres')}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white font-black text-[11px] uppercase tracking-widest px-5 py-2.5 rounded-xl shadow-lg shadow-emerald-950/20 flex items-center gap-2 transform hover:scale-[1.02] transition-all"
                    >
                        <Plus className="w-4 h-4" /> Nueva Dirección
                    </BotonAcceso>
                </div>
            </div>

            {/* CONTENT AREA */}
            <div className="grid grid-cols-1 gap-6 animate-in slide-in-from-bottom duration-1000">
                <div className={`${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200"} rounded-2xl shadow-sm border overflow-hidden`}>
                    <table className="w-full text-left">
                        <thead>
                            <tr className={`${isDarkMode ? "bg-black/20 text-slate-500 border-slate-800" : "bg-slate-50 text-slate-400 border-slate-100"} border-b text-[10px] font-black uppercase tracking-[0.15em]`}>
                                <th className="px-6 py-4">Sede / Dirección</th>
                                <th className="px-6 py-4">Localidad</th>
                                <th className="px-6 py-4">Tipo</th>
                                <th className="px-6 py-4 text-center">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className={`divide-y ${isDarkMode ? "divide-slate-800" : "divide-slate-100"}`}>
                            {filteredDirecciones.map((item) => (
                                <tr key={item.id_direccion} className="group transition-all hover:bg-emerald-500/[0.02]">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className={`p-2 rounded-lg ${isDarkMode ? "bg-slate-800 text-emerald-400" : "bg-emerald-50 text-emerald-600"}`}>
                                                <Home className="w-4 h-4" />
                                            </div>
                                            <span className="text-sm font-black tracking-tight" style={{ color: isDarkMode ? '#34d399' : '#064e3b' }}>
                                                {item.direccion}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2 text-[11px] font-bold text-slate-500">
                                            <MapPin className="w-3 h-3 text-red-500" />
                                            {item.localidad}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2 text-[11px] font-bold text-slate-500">
                                            <Building className="w-3 h-3 text-emerald-500" />
                                            Sede Operativa
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center justify-center gap-2">
                                            <Tooltip title="Editar Dirección">
                                                <button
                                                    onClick={() => navigate(`/admin/EditAddres/${item.id_direccion}`)}
                                                    className={`p-2 rounded-xl border transition-all ${isDarkMode
                                                        ? "bg-slate-800 border-slate-700 text-slate-400 hover:text-white hover:border-emerald-500/50"
                                                        : "bg-white border-slate-200 text-slate-400 hover:text-emerald-600 hover:border-emerald-200 shadow-sm"
                                                        }`}
                                                >
                                                    <FileEdit className="w-4 h-4" />
                                                </button>
                                            </Tooltip>
                                            <Tooltip title="Eliminar Dirección">
                                                <button
                                                    onClick={() => deleteAddres(item.id_direccion)}
                                                    className={`p-2 rounded-xl border transition-all ${isDarkMode
                                                        ? "bg-slate-800 border-slate-700 text-slate-400 hover:text-red-500 hover:border-red-500/50"
                                                        : "bg-white border-slate-200 text-slate-400 hover:text-red-600 hover:border-red-200 shadow-sm"
                                                        }`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </Tooltip>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {filteredDirecciones.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-20 grayscale opacity-40">
                            <Map className="w-12 h-12 mb-3 text-slate-400" />
                            <p className="text-xs font-black uppercase tracking-widest text-slate-400">No se encontraron direcciones</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default AbmDirecciones;